import React, { useState } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Check, Clock, Image as ImageIcon } from 'lucide-react';

/**
 * ProductOptionFieldRenderer - Renders dynamic product option fields for customers
 * Supports all field types including new ones: time, gallery, button-select, image-cards
 */
const ProductOptionFieldRenderer = ({ field, value, onChange, className = '' }) => {
  const fieldKey = field.name || field.id;
  const [imageError, setImageError] = useState({});

  const handleImageError = (optionValue) => {
    setImageError(prev => ({ ...prev, [optionValue]: true }));
  };

  const renderField = () => {
    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <div className={`space-y-2 ${className}`}>
            <Label htmlFor={fieldKey}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={fieldKey}
              type={field.type}
              value={value || ''}
              onChange={(e) => onChange(fieldKey, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
            />
            {field.help_text && (
              <p className="text-xs text-gray-500">{field.help_text}</p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div className={`space-y-2 ${className}`}>
            <Label htmlFor={fieldKey}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              id={fieldKey}
              value={value || ''}
              onChange={(e) => onChange(fieldKey, e.target.value)}
              placeholder={field.placeholder}
              rows={4}
              required={field.required}
            />
            {field.help_text && (
              <p className="text-xs text-gray-500">{field.help_text}</p>
            )}
          </div>
        );

      case 'date':
        return (
          <div className={`space-y-2 ${className}`}>
            <Label htmlFor={fieldKey}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={fieldKey}
              type="date"
              value={value || ''}
              onChange={(e) => onChange(fieldKey, e.target.value)}
              required={field.required}
            />
            {field.help_text && (
              <p className="text-xs text-gray-500">{field.help_text}</p>
            )}
          </div>
        );

      case 'time':
        return (
          <div className={`space-y-2 ${className}`}>
            <Label htmlFor={fieldKey} className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={fieldKey}
              type="time"
              value={value || ''}
              onChange={(e) => onChange(fieldKey, e.target.value)}
              required={field.required}
              className="text-lg"
            />
            {field.help_text && (
              <p className="text-xs text-gray-500">{field.help_text}</p>
            )}
          </div>
        );

      case 'dropdown':
        return (
          <div className={`space-y-2 ${className}`}>
            <Label htmlFor={fieldKey}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={value || ''}
              onValueChange={(val) => onChange(fieldKey, val)}
            >
              <SelectTrigger>
                <SelectValue placeholder={field.placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                    {field.price_impact && option.default_price > 0 && (
                      <span className="ml-2 text-green-600 font-semibold">
                        +₹{option.default_price}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.help_text && (
              <p className="text-xs text-gray-500">{field.help_text}</p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div className={`space-y-3 ${className}`}>
            <Label>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <RadioGroup
              value={value || ''}
              onValueChange={(val) => onChange(fieldKey, val)}
            >
              <div className="space-y-2">
                {field.options?.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <RadioGroupItem value={option.value} id={`${fieldKey}-${option.value}`} />
                    <div className="flex-1">
                      <span className="font-medium">{option.label}</span>
                      {field.price_impact && option.default_price > 0 && (
                        <span className="ml-2 text-green-600 font-semibold">
                          +₹{option.default_price}
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </RadioGroup>
            {field.help_text && (
              <p className="text-xs text-gray-500 mt-2">{field.help_text}</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div className={`space-y-3 ${className}`}>
            <Label>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2">
              {field.options?.map((option) => {
                const isChecked = Array.isArray(value) ? value.includes(option.value) : false;
                return (
                  <label
                    key={option.value}
                    className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        const currentValues = Array.isArray(value) ? value : [];
                        const newValues = checked
                          ? [...currentValues, option.value]
                          : currentValues.filter(v => v !== option.value);
                        onChange(fieldKey, newValues);
                      }}
                    />
                    <div className="flex-1">
                      <span className="font-medium">{option.label}</span>
                      {field.price_impact && option.default_price > 0 && (
                        <span className="ml-2 text-green-600 font-semibold">
                          +₹{option.default_price}
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
            {field.help_text && (
              <p className="text-xs text-gray-500 mt-2">{field.help_text}</p>
            )}
          </div>
        );

      case 'button-select':
        return (
          <div className={`space-y-3 ${className}`}>
            <Label>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="flex flex-wrap gap-3">
              {field.options?.map((option) => {
                const isSelected = value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onChange(fieldKey, option.value)}
                    className={`
                      relative px-6 py-3 rounded-lg font-medium transition-all
                      ${isSelected 
                        ? 'bg-blue-600 text-white shadow-lg scale-105' 
                        : 'bg-white border-2 border-gray-200 hover:border-blue-400 hover:shadow-md'
                      }
                    `}
                  >
                    {isSelected && (
                      <Check className="absolute top-2 right-2 h-4 w-4" />
                    )}
                    <div className="text-center">
                      <div>{option.label}</div>
                      {field.price_impact && option.default_price > 0 && (
                        <div className={`text-sm font-semibold mt-1 ${
                          isSelected ? 'text-green-200' : 'text-green-600'
                        }`}>
                          +₹{option.default_price}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {field.help_text && (
              <p className="text-xs text-gray-500 mt-2">{field.help_text}</p>
            )}
          </div>
        );

      case 'image-cards':
        return (
          <div className={`space-y-3 ${className}`}>
            <Label>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {field.options?.map((option) => {
                const isSelected = value === option.value;
                const hasImage = option.image_url && !imageError[option.value];
                
                return (
                  <Card
                    key={option.value}
                    onClick={() => onChange(fieldKey, option.value)}
                    className={`
                      relative cursor-pointer overflow-hidden transition-all
                      ${isSelected 
                        ? 'ring-4 ring-blue-500 shadow-xl scale-105' 
                        : 'hover:shadow-lg hover:scale-102'
                      }
                    `}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 z-10 bg-blue-600 rounded-full p-1">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                    
                    <div className="aspect-square relative bg-gray-100">
                      {hasImage ? (
                        <img
                          src={option.image_url}
                          alt={option.label}
                          className="w-full h-full object-cover"
                          onError={() => handleImageError(option.value)}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-12 w-12 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    <div className="p-3 text-center">
                      <p className="font-medium text-sm">{option.label}</p>
                      {field.price_impact && option.default_price > 0 && (
                        <p className="text-green-600 font-semibold text-sm mt-1">
                          +₹{option.default_price}
                        </p>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
            {field.help_text && (
              <p className="text-xs text-gray-500 mt-2">{field.help_text}</p>
            )}
          </div>
        );

      case 'gallery':
        return (
          <div className={`space-y-3 ${className}`}>
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {field.options?.map((option) => {
                const isSelected = value === option.value;
                const hasImage = option.image_url && !imageError[option.value];
                
                return (
                  <div
                    key={option.value}
                    onClick={() => onChange(fieldKey, option.value)}
                    className={`
                      relative cursor-pointer rounded-lg overflow-hidden transition-all
                      ${isSelected 
                        ? 'ring-4 ring-blue-500 shadow-lg scale-105' 
                        : 'ring-2 ring-gray-200 hover:ring-blue-300 hover:shadow-md'
                      }
                    `}
                  >
                    {isSelected && (
                      <div className="absolute top-1 right-1 z-10 bg-blue-600 rounded-full p-1">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                    
                    <div className="aspect-square relative bg-gray-100">
                      {hasImage ? (
                        <img
                          src={option.image_url}
                          alt={option.label}
                          className="w-full h-full object-cover"
                          onError={() => handleImageError(option.value)}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    {option.label && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 text-center">
                        {option.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {field.help_text && (
              <p className="text-xs text-gray-500 mt-2">{field.help_text}</p>
            )}
          </div>
        );

      default:
        return (
          <div className={className}>
            <p className="text-sm text-gray-500">Unsupported field type: {field.type}</p>
          </div>
        );
    }
  };

  return renderField();
};

export default ProductOptionFieldRenderer;
